import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { organogramasService } from '@/services/api';
import { Organograma } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, Filter, Loader2, Network, Search, X } from 'lucide-react';
import { MvpCardNode, MvpColumnNode, MvpFigureNode } from '@/components/organograma/OrganogramaNodes';
import { applyEdgeTipoStyle, mapApiEdgesToFlow, mapApiNodesToFlow } from '@/components/organograma/organogramaMappers';
import {
  buildChildrenMap,
  countDirectReports,
  getCardIds,
  getDescendants,
  isHierarchyEdge,
  nodeSizeOf,
  normalizeSearchText,
} from '@/components/organograma/organogramaGraph';
import type { OrgMvpApiEdge, OrgMvpApiNode, OrgMvpCardData, OrgMvpColumnData, OrgMvpFigureData, OrgMvpNode } from '@/components/organograma/organogramaTypes';

type PublicCanvasPayload = Organograma & {
  readonly: boolean;
  canEdit: boolean;
  nodes: PublicCanvasNode[];
  edges: PublicCanvasEdge[];
};

type PublicCanvasNode = OrgMvpApiNode;
type PublicCanvasEdge = OrgMvpApiEdge;

const nodeTypes = {
  card: MvpCardNode,
  column: MvpColumnNode,
  figure: MvpFigureNode,
};

const toReadonlyFlowNodes = (nodes: PublicCanvasNode[]): OrgMvpNode[] =>
  mapApiNodesToFlow(nodes).map((node) => ({
    ...node,
    draggable: false,
    selectable: false,
    data: {
      ...node.data,
      readonly: true,
    },
  }));

function PublicCanvas({ organograma }: { organograma: PublicCanvasPayload }) {
  const { setCenter } = useReactFlow();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [departmentFilterIds, setDepartmentFilterIds] = useState<Set<string>>(new Set());
  const [hideFiltered, setHideFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const baseNodes = useMemo(() => toReadonlyFlowNodes(organograma.nodes || []), [organograma.nodes]);
  const baseEdges = useMemo<Edge[]>(() => mapApiEdgesToFlow(organograma.edges || []), [organograma.edges]);
  const apiNodeById = useMemo(
    () => new Map((organograma.nodes || []).map((node) => [node.id, node])),
    [organograma.nodes],
  );

  const departamentoOptions = useMemo(() => {
    const options = new Map<string, string>();
    (organograma.nodes || []).forEach((node) => {
      if (node.departamento?.id && node.departamento.nome) {
        options.set(node.departamento.id, node.departamento.nome);
      }
    });
    return [...options.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [organograma.nodes]);

  const cardIds = useMemo(() => getCardIds(baseNodes), [baseNodes]);
  const childrenMap = useMemo(() => buildChildrenMap(baseEdges, cardIds), [baseEdges, cardIds]);
  const parentsMap = useMemo(() => {
    const map = new Map<string, string[]>();
    baseEdges.forEach((edge) => {
      if (!isHierarchyEdge(edge, cardIds)) return;
      map.set(edge.target, [...(map.get(edge.target) || []), edge.source]);
    });
    return map;
  }, [baseEdges, cardIds]);
  const directReportsMap = useMemo(() => countDirectReports(baseEdges, cardIds), [baseEdges, cardIds]);

  const hiddenByCollapse = useMemo(() => {
    const hidden = new Set<string>();
    collapsedIds.forEach((id) => {
      getDescendants(id, childrenMap).forEach((descendantId) => hidden.add(descendantId));
    });
    return hidden;
  }, [childrenMap, collapsedIds]);

  const filterActive = departmentFilterIds.size > 0;

  const dimmedNodeIds = useMemo(() => {
    if (!filterActive) return new Set<string>();
    const dimmed = new Set<string>();
    baseNodes.forEach((node) => {
      if (node.type !== 'card') return;
      const apiNode = apiNodeById.get(node.id);
      const departamentoId = apiNode?.departamento?.id || apiNode?.departamento_id || null;
      if (!departamentoId || !departmentFilterIds.has(departamentoId)) dimmed.add(node.id);
    });
    return dimmed;
  }, [apiNodeById, baseNodes, departmentFilterIds, filterActive]);

  const hiddenNodeIds = useMemo(() => {
    if (!filterActive || !hideFiltered) return hiddenByCollapse;
    const hidden = new Set(hiddenByCollapse);
    dimmedNodeIds.forEach((id) => hidden.add(id));
    return hidden;
  }, [dimmedNodeIds, filterActive, hiddenByCollapse, hideFiltered]);

  const handleToggleCollapse = useCallback((nodeId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const flowNodes = useMemo(
    () =>
      baseNodes.map((node) => {
        const base = { ...node, hidden: hiddenNodeIds.has(node.id) };
        if (node.type !== 'card') return base;
        const data = node.data as OrgMvpCardData;
        const apiNode = apiNodeById.get(node.id);
        const isCollapsed = collapsedIds.has(node.id);
        return {
          ...base,
          data: {
            ...base.data,
            directReports: directReportsMap.get(node.id) || 0,
            hasChildren: (childrenMap.get(node.id) || []).length > 0,
            isCollapsed,
            collapsedCount: isCollapsed ? getDescendants(node.id, childrenMap).size : 0,
            onToggleCollapse: handleToggleCollapse,
            // Sem e-mail no público, por privacidade
            quickView: {
              nome: data.label,
              cargo: data.subtitle || null,
              departamento: apiNode?.departamento?.nome || null,
              email: null,
            },
            highlighted: highlightedNodeId === node.id,
            dimmed: dimmedNodeIds.has(node.id) && !(filterActive && hideFiltered),
          },
        };
      }),
    [
      apiNodeById,
      baseNodes,
      childrenMap,
      collapsedIds,
      dimmedNodeIds,
      directReportsMap,
      filterActive,
      handleToggleCollapse,
      hiddenNodeIds,
      hideFiltered,
      highlightedNodeId,
    ],
  );

  const flowEdges = useMemo(
    () =>
      baseEdges.map((edge) => {
        const styled = applyEdgeTipoStyle(edge);
        const hidden = hiddenNodeIds.has(edge.source) || hiddenNodeIds.has(edge.target);
        const dim = dimmedNodeIds.has(edge.source) || dimmedNodeIds.has(edge.target);
        return {
          ...styled,
          hidden,
          style: dim ? { ...styled.style, opacity: 0.2 } : styled.style,
        };
      }),
    [baseEdges, dimmedNodeIds, hiddenNodeIds],
  );

  const searchMatches = useMemo(() => {
    const query = normalizeSearchText(searchQuery.trim());
    if (!query) return [] as OrgMvpNode[];
    return baseNodes.filter((node) => {
      if (node.type === 'column') {
        const data = node.data as OrgMvpColumnData;
        return normalizeSearchText(data.title || '').includes(query);
      }
      if (node.type === 'figure') {
        const data = node.data as OrgMvpFigureData;
        return normalizeSearchText(data.label || '').includes(query);
      }
      const data = node.data as OrgMvpCardData;
      return (
        normalizeSearchText(data.label || '').includes(query)
        || normalizeSearchText(data.subtitle || '').includes(query)
      );
    });
  }, [baseNodes, searchQuery]);

  useEffect(() => {
    setSearchIndex(0);
  }, [searchQuery]);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
  }, []);

  const focusSearchResult = useCallback(
    (index: number) => {
      const node = searchMatches[index];
      if (!node) return;

      setCollapsedIds((current) => {
        if (current.size === 0) return current;
        const next = new Set(current);
        const seen = new Set<string>();
        const stack = [node.id];
        while (stack.length > 0) {
          const currentId = stack.pop();
          if (!currentId) continue;
          (parentsMap.get(currentId) || []).forEach((parentId) => {
            if (seen.has(parentId)) return;
            seen.add(parentId);
            next.delete(parentId);
            stack.push(parentId);
          });
        }
        return next;
      });

      const { width, height } = nodeSizeOf(node);
      setCenter(node.position.x + width / 2, node.position.y + height / 2, { zoom: 1.1, duration: 400 });
      setHighlightedNodeId(node.id);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedNodeId(null), 2400);
    },
    [parentsMap, searchMatches, setCenter],
  );

  const handleSearchNavigate = useCallback(
    (direction: 1 | -1) => {
      if (searchMatches.length === 0) return;
      const nextIndex = (searchIndex + direction + searchMatches.length) % searchMatches.length;
      setSearchIndex(nextIndex);
      focusSearchResult(nextIndex);
    },
    [focusSearchResult, searchIndex, searchMatches.length],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border bg-muted/20 px-2 py-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (searchMatches.length === 0) return;
                  const current = searchMatches[searchIndex];
                  if (current && highlightedNodeId === current.id) {
                    handleSearchNavigate(event.shiftKey ? -1 : 1);
                  } else {
                    focusSearchResult(searchIndex);
                  }
                }
              }}
              placeholder="Buscar pessoa ou área"
              className="h-9 w-[200px] rounded-md bg-background pl-8 text-sm"
            />
          </div>
          {searchQuery && (
            <>
              <span className="text-xs tabular-nums text-muted-foreground">
                {searchMatches.length > 0 ? `${searchIndex + 1}/${searchMatches.length}` : '0'}
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleSearchNavigate(-1)} disabled={searchMatches.length === 0}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleSearchNavigate(1)} disabled={searchMatches.length === 0}>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {departamentoOptions.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn('h-9', filterActive && 'border-primary/60 text-primary')}
              >
                <Filter className="mr-2 h-4 w-4" />
                Departamentos
                {filterActive && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[11px]">
                    {departmentFilterIds.size}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Filtrar por departamento</h3>
                {filterActive && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setDepartmentFilterIds(new Set())}>
                    <X className="mr-1 h-3 w-3" />
                    Limpar
                  </Button>
                )}
              </div>
              <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                {departamentoOptions.map((departamento) => (
                  <label key={departamento.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={departmentFilterIds.has(departamento.id)}
                      onCheckedChange={() =>
                        setDepartmentFilterIds((current) => {
                          const next = new Set(current);
                          if (next.has(departamento.id)) next.delete(departamento.id);
                          else next.add(departamento.id);
                          return next;
                        })
                      }
                    />
                    <span className="truncate">{departamento.nome}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
                <Label htmlFor="public-hide-filtered" className="text-xs text-muted-foreground">
                  Ocultar não correspondentes
                </Label>
                <Switch id="public-hide-filtered" checked={hideFiltered} onCheckedChange={setHideFiltered} />
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="h-[78vh] overflow-hidden rounded-xl border bg-white">
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
    </div>
  );
}

export default function OrganogramaPublico() {
  const { token } = useParams();
  const [organograma, setOrganograma] = useState<PublicCanvasPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        setIsLoading(true);
        const result = await organogramasService.getPublicCanvasByToken(token);
        if (result.success && result.data) setOrganograma(result.data);
        else setError(result.error || 'Link inválido ou expirado');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [token]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !organograma) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md rounded-lg border bg-card p-8 text-center">
          <Network className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Organograma indisponível</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error || 'Link inválido ou expirado'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">{organograma.nome}</h1>
            <Badge variant="outline">Somente leitura</Badge>
          </div>
          {organograma.descricao && <p className="text-muted-foreground">{organograma.descricao}</p>}
        </div>

        <ReactFlowProvider>
          <PublicCanvas organograma={organograma} />
        </ReactFlowProvider>
      </div>
    </div>
  );
}
